import { AccountPage, api, type AuthConfig, dateTime, Empty, ExtranetShell, go, LoginPage, NavGroup, NavLink, roleSummary, setUnauthorizedHandler, useAsync, UsersPage, useRoute, type Me } from "@getaways/extranet-ui";
import { IconBed, IconBuildingStore, IconCash, IconBell, IconChartBar, IconFilter, IconMessageQuestion, IconClockPlay, IconDatabase, IconListDetails, IconPlaneDeparture, IconTrendingUp, IconUsers } from "@tabler/icons-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { canWith, MeContext, ROLE_NAMES, rolesFor } from "./lib/access";
import { AlertsPage } from "./pages/Alerts";
import { AskPage } from "./pages/Ask";
import { BookingsPage } from "./pages/Bookings";
import { SchedulesPage } from "./pages/Schedules";
import { FinancePage } from "./pages/Finance";
import { FunnelPage } from "./pages/Funnel";
import { OperationsPage } from "./pages/Operations";
import { OverviewPage } from "./pages/Overview";
import { SalesPage } from "./pages/Sales";
import { SuppliersPage } from "./pages/Suppliers";
import { HotelsPage } from "./pages/Hotels";
import { StatusPage, type Status } from "./pages/Status";

/** Brand resources a grant can name (BRAND_RESOURCES in @getaways/reports). */
const BRAND_RESOURCES = [
  { id: "7e9b0000-0000-4000-8000-00000000a201", name: "Azores Getaways" },
  { id: "7e9b0000-0000-4000-8000-00000000a202", name: "Portugal Getaways" },
  { id: "7e9b0000-0000-4000-8000-00000000a203", name: "Yes Getaways" },
];

export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [auth, setAuth] = useState<AuthConfig>({ mode: "local" });
  const route = useRoute();
  const page = route[0] ?? "overview";

  const start = useCallback(async () => {
    await api<AuthConfig>("GET", "/auth/config").then(setAuth).catch(() => undefined);
    try {
      const who = await api<Me | { service: true }>("GET", "/auth/me");
      setMe("service" in who ? null : who);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
    void start();
  }, [start]);

  const status = useAsync(() => (me ? api<Status>("GET", "/v1/status") : Promise.resolve(undefined)), [me, route.join("/")]);

  if (me === undefined) return <div className="empty">Loading…</div>;
  if (me === null) return <LoginPage product="Ideastation Reports" title="Sign in to Ideastation Reports" headline="Every sale, hotel and euro, in one place." tagline="Reports and dashboards for the Ideastation team, across brands and products." onLoggedIn={start} auth={auth} />;

  const logout = async () => {
    const res = await api<{ redirect?: string }>("POST", "/auth/logout").catch(() => undefined);
    if (res?.redirect) return window.location.assign(res.redirect);
    setMe(null);
    go("overview");
  };
  const manageUsers = canWith(me, "users:manage") && auth.mode === "local";

  let content: ReactNode;
  const all = canWith(me, "sales:all");
  const finance = canWith(me, "finance:read");
  if (page === "overview") content = <OverviewPage />;
  else if (page === "sales") content = <SalesPage />;
  else if (page === "hotels") content = <HotelsPage />;
  else if (page === "suppliers") content = all ? <SuppliersPage /> : <Empty>Not available for your role.</Empty>;
  else if (page === "operations") content = all ? <OperationsPage /> : <Empty>Not available for your role.</Empty>;
  else if (page === "funnel") content = all ? <FunnelPage /> : <Empty>Not available for your role.</Empty>;
  else if (page === "finance") content = finance ? <FinancePage /> : <Empty>Finance and management only.</Empty>;
  else if (page === "bookings") content = <BookingsPage />;
  else if (page === "ask") content = <AskPage />;
  else if (page === "schedules") content = <SchedulesPage />;
  else if (page === "alerts") content = <AlertsPage />;
  else if (page === "status") content = <StatusPage onSynced={status.reload} />;
  else if (page === "users") content = manageUsers ? <UsersPage me={me.user} roleNames={ROLE_NAMES} rolesFor={rolesFor} resources={BRAND_RESOURCES.map((b) => ({ ...b, organizationId: me.user.memberships[0]?.organizationId ?? "" }))} resourceKey="brandIds" resourceLabel="Brands" /> : <Empty>Administrators only.</Empty>;
  else if (page === "conta") content = <AccountPage user={me.user} roleNames={ROLE_NAMES} auth={auth} />;
  else content = <Empty>Page not found.</Empty>;

  const s = status.data;
  const nav = (
    <>
      <NavLink to="overview" page={page} icon={<IconChartBar size={18} />} label="Overview" />
      <NavLink to="sales" page={page} icon={<IconTrendingUp size={18} />} label="Sales" />
      <NavLink to="hotels" page={page} icon={<IconBed size={18} />} label="Hotels" />
      {all && <NavLink to="suppliers" page={page} icon={<IconBuildingStore size={18} />} label="Suppliers" />}
      {all && <NavLink to="operations" page={page} icon={<IconPlaneDeparture size={18} />} label="Operations" />}
      {all && <NavLink to="funnel" page={page} icon={<IconFilter size={18} />} label="Funnel" />}
      {finance && <NavLink to="finance" page={page} icon={<IconCash size={18} />} label="Finance" />}
      <NavLink to="bookings" page={page} icon={<IconListDetails size={18} />} label="Bookings" />
      <NavLink to="ask" page={page} icon={<IconMessageQuestion size={18} />} label="Ask" />
      <NavGroup>Sending</NavGroup>
      <NavLink to="schedules" page={page} icon={<IconClockPlay size={18} />} label="Schedules" />
      <NavLink to="alerts" page={page} icon={<IconBell size={18} />} label="Alerts" />
      <NavGroup>Data</NavGroup>
      <NavLink to="status" page={page} icon={<IconDatabase size={18} />} label="Data status" />
      {manageUsers && <NavLink to="users" page={page} icon={<IconUsers size={18} />} label="Users" />}
      {s && <div className={`asof ${s.late ? "late" : ""}`} style={{ padding: "12px 14px" }}>{s.asOf ? `Data as of ${dateTime(s.asOf, "Europe/Lisbon")}` : "Not synced yet"}{s.late ? " · late" : ""}</div>}
    </>
  );

  return (
    <MeContext.Provider value={me}>
      <ExtranetShell product="Ideastation Reports" user={me.user} role={roleSummary(me.user.memberships, ROLE_NAMES)} onLogout={() => void logout()} portalUrl={auth.mode === "central" ? auth.portalUrl : undefined} nav={nav}>
        {content}
      </ExtranetShell>
    </MeContext.Provider>
  );
}
